///
/// EarthBrowser
///
/// Created by Matt Giger on 9/18/2026
/// Copyright (c) 2026 Geodata Labs
///
/// GPU texture memory budgeting. Mobile browsers (especially iOS Safari,
/// which exposes no heap or memory-pressure APIs) kill the tab without
/// warning when a page allocates too much, so the only reliable strategy is
/// to budget against our own allocation counter (TextureMemory in stovokor):
///
/// - Soft budget: above this, texture series shrink their preload windows
///   (graceful degradation - shorter animation lookahead, same visuals)
/// - Hard budget: enabling a dataset whose predicted footprint would land
///   above this is blocked at the redux layer with a toast, before any
///   allocation happens
///
/// Budgets cover engine texture memory only; JS heap, vector tiles and
/// driver overhead are extra, which is why these numbers leave generous
/// headroom below observed tab-kill thresholds (~1-1.5GB total on iOS).
///
/// Budgets depend on the device only. Crash history does not shrink them:
/// that locked users out of layers for days after one suspected kill.
/// CrashRecovery instead puts the *current session* in lite mode when a
/// crash loop is under way, which the soft-budget degradation path honors
/// (every texture series runs its minimum preload window) and which ends
/// with the session.

import { textureMemoryStats } from "@stovokor/shader/TextureMemory"
import { type DatasetType } from "@/types/DatasetElement"
import { isMemoryConstrainedDevice } from "@/earthbrowser/utils/MemoryLog"
import { isMobile } from "@/earthbrowser/utils/platform"

const cMB = 1024 * 1024

// Debug override (set via window.__ebDebug.setGpuBudgetMb) so gating and
// degradation can be exercised on desktop without a real low-memory device
let budgetOverride: { softBytes: number, hardBytes: number } | null = null

export function setGpuBudgetOverride(softBytes: number, hardBytes: number): void {
	budgetOverride = { softBytes, hardBytes }
}

export function clearGpuBudgetOverride(): void {
	budgetOverride = null
}

/**
 * Lite mode: the session behaves as if permanently over the soft budget,
 * so texture series keep only the next frame ahead and nothing behind
 * (same visuals, shorter animation lookahead). Set by CrashRecovery when
 * the previous sessions formed a crash loop, or by a native memory
 * warning; not persisted - a fresh launch decides again from its verdict.
 */
let liteMode = false

export function setLiteMode(on: boolean): void {
	if (on === liteMode) return
	liteMode = on
	console.info(`[MemoryBudget] lite mode ${on ? "on" : "off"}`)
}

export function isLiteMode(): boolean {
	return liteMode
}

/** Soft budget: above this, series shrink preload windows */
export function softBudgetBytes(): number {
	if (budgetOverride) return budgetOverride.softBytes
	return (isMemoryConstrainedDevice() ? 256 : 2048) * cMB
}

/** Hard budget: enables that would predictably exceed this are blocked.
 *  Mobile tabs are observed to die around ~1GB total, so 700MB of texture
 *  memory leaves room for JS heap, vector tiles and driver overhead. */
export function hardBudgetBytes(): number {
	if (budgetOverride) return budgetOverride.hardBytes
	return (isMemoryConstrainedDevice() ? 700 : 3072) * cMB
}

/** True when texture series should run their minimum preload windows:
 *  texture memory is over the soft budget, or the session is in lite mode */
export function underMemoryPressure(): boolean {
	return liteMode || textureMemoryStats().bytes > softBudgetBytes()
}

/**
 * Rough additional GPU texture bytes a dataset holds resident once enabled
 * (preload window x per-frame size, from the layers' formats and mobile
 * frame caps). Planning numbers for the enable gate, not exact accounting -
 * actual usage is tracked exactly by TextureMemory once textures load.
 *
 * `lite` is the footprint under lite mode (TextureSeries.effectiveWindow:
 * two frames ahead, none behind): the mobile number on desktop, and three
 * quarters of it on mobile, whose windows are already near the minimum.
 */
export function estimatedDatasetBytes(type: DatasetType, lite: boolean = false): number {
	const mobile = isMobile()
	const pick = (mobileMb: number, desktopMb: number): number => {
		if (lite) return Math.ceil(mobile ? mobileMb * 0.75 : mobileMb) * cMB
		return (mobile ? mobileMb : desktopMb) * cMB
	}
	switch (type) {
		case "cloud":
			// 4096x2048 R8 + mips (~11MB) x preload window (2 mobile / 5 desktop)
			return pick(22, 56)
		case "radar":
			// R8 equirect mosaic window plus WebMercator refinement tiles
			return pick(32, 64)
		case "cloud_forecast":
			// 2048x2048 R8 + mips (~5.6MB) x the region series' preload window
			return pick(12, 36)
		case "radar_forecast":
			// 2442x1051 R8 CONUS frames (~2.6MB) x preload window for the
			// reflectivity and probability series plus RG32F flow textures
			return pick(16, 40)
		case "precipitation":
			// IMERG R8 window plus RG32F flow textures (8 bytes/pixel)
			return pick(48, 96)
		case "sst":
			// R8 daily frames (10 mobile / 30 desktop)
			return pick(24, 64)
		case "nexrad":
			// 1920x1920 RGBA atlases (~14MB) x ~8-frame window per visible
			// station; wide views stream several stations at once
			return pick(128, 256)
		case "synoptic":
			// Pressure field window (fieldDownsample=2 on constrained devices)
			return pick(12, 32)
		case "jetstream":
			// 0.5 deg RGBA8 wind frames (~1MB each) in the same residency window
			return pick(4, 10)
		case "aurora":
			// Baked RG8 probability field with mips (1024 or 512 px per hemisphere)
			return pick(2, 6)
		case "upper_air":
			// 0.5 deg R32F height frames (~1MB each) in the same residency window
			return pick(4, 10)
		default:
			// Entity layers (markers, labels, polygons): small GPU footprint
			return 8 * cMB
	}
}

/**
 * Datasets with a large resident GPU footprint (per estimatedDatasetBytes).
 * These are the layers CrashRecovery brings back one at a time after a
 * crash (and the ones the sentinel tracks as likely culprits).
 */
export function isHeavyDataset(type: DatasetType): boolean {
	switch (type) {
		case "cloud":
		case "radar":
		case "precipitation":
		case "sst":
		case "nexrad":
		case "synoptic":
			return true
		default:
			return false
	}
}

/** Display names for gate feedback (toast copy) */
export function datasetDisplayName(type: DatasetType): string {
	switch (type) {
		case "cloud": return "Clouds"
		case "precipitation": return "Precipitation"
		case "radar": return "Radar"
		case "radar_forecast": return "Radar Forecast"
		case "cloud_forecast": return "Cloud Forecast"
		case "nexrad": return "3D Radar"
		case "sst": return "Sea Temperature"
		case "synoptic": return "Synoptic Weather"
		case "city_weather": return "City Weather"
		default: return type.charAt(0).toUpperCase() + type.slice(1)
	}
}

export interface EnableVerdict {
	/** Fits as the session currently runs (full windows, or lite if already on) */
	allowed: boolean
	/** Doesn't fit now, but would with the session in lite mode */
	liteAllowed: boolean
	currentBytes: number
	predictedBytes: number
	predictedLiteBytes: number
	hardBudget: number
}

/**
 * Whether enabling a dataset is predicted to stay inside the hard budget.
 * Compares exact current usage plus the dataset's estimated footprint, at
 * full preload windows and at lite-mode windows, so the gate can answer
 * "yes, in reduced-memory mode" before it answers "no".
 *
 * Only heavy (texture-series) datasets are gated. Entity layers hold a few
 * MB of markers at most, so blocking them never prevents a crash - it only
 * locks the user out when the baseline is high for reasons the gate can't
 * fix (model textures, tiles).
 */
export function canEnableDataset(type: DatasetType): EnableVerdict {
	const currentBytes = textureMemoryStats().bytes
	const predictedBytes = currentBytes + estimatedDatasetBytes(type)
	const predictedLiteBytes = currentBytes + estimatedDatasetBytes(type, true)
	const hardBudget = hardBudgetBytes()
	const heavy = isHeavyDataset(type)
	const fitsNow = (liteMode ? predictedLiteBytes : predictedBytes) <= hardBudget
	return {
		allowed: !heavy || fitsNow,
		liteAllowed: heavy && !fitsNow && predictedLiteBytes <= hardBudget,
		currentBytes,
		predictedBytes,
		predictedLiteBytes,
		hardBudget,
	}
}
